/*
  drs-pdp-colour — opens the Colour Gallery and makes the product page react to
  the chosen colour.

  Two jobs:

  1. GALLERY. The modal is the theme's own <modal-dialog>, so opening it is just
     a click on the matching [data-modal] opener pattern. Picking a colour finds
     the matching radio in the variant picker and clicks it — driving Shopify's
     normal variant flow (which re-renders price, media and availability) rather
     than running a parallel one.

  2. ACCENT. assets/figs-accent.js already publishes window.figsAccent.tint(hex)
     on every page; only the bespoke FIGS sections ever called it. Here it is
     called on variant change so the standard product page behaves the same.

  The raw-value -> {name, hex} pairs come from the snippet, which builds them
  from drs-facet-map.liquid, so a colour's hex here is the same one the filter
  swatches and cart use.
*/
(() => {
  const parsePairs = (raw) => {
    const map = new Map();
    (raw || '').split('\n').forEach((line) => {
      const [value, name, hex] = line.split('||');
      if (value) map.set(value.trim(), { name: (name || '').trim(), hex: (hex || '').trim() });
    });
    return map;
  };

  const root = () => document.querySelector('[data-drs-colour-gallery]');

  /** The colour radio currently selected in the variant picker. */
  const selected = (position) => {
    const field = document.querySelector(`.option-selector[data-index="${position}"]`);
    if (!field) return null;
    return field.querySelector('.js-option:checked, .js-option[aria-selected="true"]');
  };

  const applyAccent = () => {
    const el = root();
    if (!el || !window.figsAccent) return;
    const pairs = parsePairs(el.dataset.pairs);
    const input = selected(el.dataset.optionPosition);
    if (!input) return;
    const entry = pairs.get(input.value);
    if (!entry || !entry.hex) return;

    const hex = entry.hex;
    // Announcement bar + Judge.me widget.
    window.figsAccent.tint(hex);

    // Add to Cart, tinted the same way sections/figs-product.liquid does it.
    const atc = document.querySelector('.section-main-product .add-to-cart');
    if (!atc) return;
    const fg = window.figsAccent.textFor(hex);
    atc.style.setProperty('background', hex, 'important');
    atc.style.setProperty('border-color', hex, 'important');
    atc.style.color = fg;
    // A pale colourway (White is a real one here) would paint a white button on
    // a white page. Outline it whenever the accent is light enough to need dark
    // text — the same guard the FIGS page uses.
    atc.style.boxShadow = fg === '#111' ? 'inset 0 0 0 1px #111' : '';

    // Buy it Now is deliberately left alone. It stays black so there is one
    // fixed high-contrast action next to the colour-shifting Add to Cart.
  };

  // Opening is handled by the theme's <modal-opener> in the snippet — nothing
  // to do here. This only handles picking a colour from inside the gallery.
  document.addEventListener('click', (evt) => {
    const pick = evt.target.closest('[data-drs-cg-pick]');
    if (!pick) return;
    const el = root();
    if (!el) return;
    const field = document.querySelector(`.option-selector[data-index="${el.dataset.optionPosition}"]`);
    if (!field) return;
    const target = [...field.querySelectorAll('.js-option')]
      .find((i) => i.value === pick.dataset.drsCgPick);
    if (target) {
      target.click();
      const modal = document.querySelector('.drs-cg-modal');
      if (modal && typeof modal.close === 'function') modal.close();
      else if (modal) modal.classList.remove('active');
    }
  });

  // Re-tint whenever a variant option changes, and once on load for the
  // pre-selected colour. Delegated so it survives the picker re-rendering
  // itself from the Section Rendering API after a variant switch.
  document.addEventListener('change', (evt) => {
    if (evt.target.closest('.option-selector')) applyAccent();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyAccent);
  } else {
    applyAccent();
  }
})();
